import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Button, Spinner, Alert, Badge } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

export const AdminSystem = () => {
  const [healthStatus, setHealthStatus] = useState(null);
  const [ctracInfo, setCtracInfo] = useState(null);
  const [analysisPackageInfo, setAnalysisPackageInfo] = useState(null);
  const [calculationPackageInfo, setCalculationPackageInfo] = useState(null);
  const [comparisonStorageInfo, setComparisonStorageInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [analysisDeleting, setAnalysisDeleting] = useState(false);
  const [calculationDeleting, setCalculationDeleting] = useState(false);
  const [comparisonDeleting, setComparisonDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [cleanupSuccess, setCleanupSuccess] = useState(null);
  const [packageSuccess, setPackageSuccess] = useState(null);


  useEffect(() => {
    loadSystemInfo();
  }, []);

  const loadSystemInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load health status and CTRAC info in parallel
      const [health, ctrac, analysisPkg, calcPkg, comparisonStorage] = await Promise.all([
        apiClient.getSystemHealth(),
        apiClient.getCtracPackageInfo(),
        apiClient.getAnalysisPackageInfo(),
        apiClient.getCalculationPackageInfo(),
        apiClient.getComparisonStorageInfo()
      ]);

      setHealthStatus(health);
      setCtracInfo(ctrac);
      setAnalysisPackageInfo(analysisPkg);
      setCalculationPackageInfo(calcPkg);
      setComparisonStorageInfo(comparisonStorage);
    } catch (exc) {
      console.error('Failed to load system info:', exc);
      setError(exc.message || 'Failed to load system information');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Are you sure you want to cleanup the entire WiseApp AppDirectory? This will delete all files in the directory and cannot be undone!')) {
      return;
    }

    try {
      setCleanupLoading(true);
      setCleanupSuccess(null);
      setError(null);

      const result = await apiClient.cleanupAppDirectory();

      setCleanupSuccess(result.message || 'Cleanup completed successfully');

      // Refresh CTRAC info after cleanup
      setTimeout(() => {
        loadSystemInfo();
      }, 1000);
    } catch (exc) {
      console.error('Failed to cleanup:', exc);
      setError(exc.message || 'Failed to cleanup AppDirectory');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleDeletePackage = async (kind) => {
    const isAnalysis = kind === 'analysis';
    const label = isAnalysis ? 'Analysis CTRAC package (CSV format)' : 'Calculation CTRAC package (CTRAC format)';
    const action = apiClient[isAnalysis ? 'deleteAnalysisPackage' : 'deleteCalculationPackage'].bind(apiClient);

    // The "Calculation" delete is recursive across the whole Calculation
    // workspace (CTRAC + OriginalCTRAC + any sibling caches), so the
    // confirm prompt must spell that out instead of implying we only
    // wipe the generated CTRAC extraction.
    const confirmMessage = isAnalysis
      ? `Delete ${label}? This removes the locally generated folder. It will be regenerated automatically the next time it is needed.`
      : `Delete ${label}? This recursively removes the entire Calculation workspace (generated CTRAC, original CTRAC extraction, and any sibling caches). It will be regenerated automatically the next time it is needed.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (isAnalysis) setAnalysisDeleting(true); else setCalculationDeleting(true);
      setPackageSuccess(null);
      setError(null);

      const result = await action();

      setPackageSuccess(
        result.deleted
          ? `${label} deleted (${result.path}).`
          : `${label} was already absent; nothing to delete.`
      );

      // Refresh package info after delete
      setTimeout(() => {
        loadSystemInfo();
      }, 500);
    } catch (exc) {
      console.error(`Failed to delete ${kind} package:`, exc);
      setError(exc.message || `Failed to delete ${kind} package`);
    } finally {
      if (isAnalysis) setAnalysisDeleting(false); else setCalculationDeleting(false);
    }
  };

  // Comparison CSV cache — wipes every Model-{id}-{Mode}-{Display|Raw}.csv
  // and matching sidecar so a fresh build starts from a clean slate.
  // The folder itself stays in place (it will be recreated lazily on
  // the next "DB vs Original CTRAC" click). The user gets a normal
  // browser confirm() guard so this can never run by accident.
  const handleDeleteComparisonStorage = async () => {
    if (!window.confirm('Delete every cached comparison CSV / sidecar file? The next "DB vs Original CTRAC" build will regenerate them. This cannot be undone.')) {
      return;
    }

    try {
      setComparisonDeleting(true);
      setPackageSuccess(null);
      setError(null);

      const result = await apiClient.deleteComparisonStorage();
      const deletedCount = result.deleted ?? 0;
      setPackageSuccess(
        deletedCount > 0
          ? `Removed ${deletedCount} comparison cache file(s) from ${result.path}.`
          : `Comparison cache was already empty; nothing to delete.`
      );

      // Refresh comparison info after delete
      setTimeout(() => {
        loadSystemInfo();
      }, 500);
    } catch (exc) {
      console.error('Failed to delete comparison cache:', exc);
      setError(exc.message || 'Failed to delete comparison cache');
    } finally {
      setComparisonDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Render byte counts as the most natural unit (B / KB / MB / GB). The
  // comparison cache is currently < 100 MB even on the largest models,
  // so we don't bother with TB.
  const formatBytes = (bytes) => {
    if (bytes == null || isNaN(bytes) || bytes < 0) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">System Status</h2>
        <div className="d-flex gap-2">
          <Button color="primary" onClick={loadSystemInfo} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Refreshing...
              </>
            ) : (
              'Refresh'
            )}
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="danger" className="mb-4" fade={false}>
          <h5>Error</h5>
          <p className="mb-0">{error}</p>
        </Alert>
      )}

      {cleanupSuccess && (
        <Alert color="success" className="mb-4" fade={false} toggle={() => setCleanupSuccess(null)}>
          <h5>Success</h5>
          <p className="mb-0">{cleanupSuccess}</p>
        </Alert>
      )}

      {packageSuccess && (
        <Alert color="success" className="mb-4" fade={false} toggle={() => setPackageSuccess(null)}>
          <h5>Success</h5>
          <p className="mb-0">{packageSuccess}</p>
        </Alert>
      )}

      <Row>
        {/* Health Status Card */}
        <Col md={6} className="mb-4">
          <Card>
            <CardHeader>
              <h5 className="mb-0">System Health</h5>
            </CardHeader>
            <CardBody>
              {loading && !healthStatus ? (
                <div className="text-center py-3">
                  <Spinner color="primary" />
                </div>
              ) : healthStatus ? (
                <div>
                  <div className="mb-3">
                    <strong>Status: </strong>
                    {healthStatus.healthy ? (
                      <Badge color="success" className="ms-2">Healthy</Badge>
                    ) : (
                      <Badge color="danger" className="ms-2">Unhealthy</Badge>
                    )}
                  </div>
                  <div className="mb-3">
                    <strong>Message:</strong>
                    <p className="mb-0 mt-1 text-muted small">{healthStatus.message}</p>
                  </div>
                  <div className="text-muted small">
                    <strong>Last Checked:</strong> {formatDate(healthStatus.timestamp)}
                  </div>
                </div>
              ) : (
                <div className="text-muted">No health data available</div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* CTRAC Package Info Card */}
        <Col md={6} className="mb-4">
          <Card>
            <CardHeader>
              <h5 className="mb-0">CTRAC Package Info</h5>
            </CardHeader>
            <CardBody>
              {loading && !ctracInfo ? (
                <div className="text-center py-3">
                  <Spinner color="primary" />
                </div>
              ) : ctracInfo ? (
                <div>
                  {ctracInfo.found ? (
                    <>
                      <div className="mb-3">
                        <strong>Status: </strong>
                        <Badge color="success" className="ms-2">Found</Badge>
                      </div>
                      <div className="mb-3">
                        <strong>File Name:</strong>
                        <p className="mb-0 mt-1 text-muted small">
                          {ctracInfo.fileName}
                        </p>
                      </div>
                      <div className="mb-3">
                        <strong>Full Path:</strong>
                        <p className="mb-0 mt-1 text-muted small" style={{ wordBreak: 'break-all' }}>
                          {ctracInfo.fullPath}
                        </p>
                      </div>
                      <div className="mb-3">
                        <strong>Last Modified:</strong>
                        <p className="mb-0 mt-1">{formatDate(ctracInfo.lastModified)}</p>
                      </div>
                      <div className="mb-3">
                        <strong>File Size:</strong>
                        <p className="mb-0 mt-1">{ctracInfo.fileSizeBytes ? (ctracInfo.fileSizeBytes / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}</p>
                      </div>
                      <div className="text-muted small">
                        <strong>Date:</strong> {ctracInfo.currentDate}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-3">
                        <strong>Status: </strong>
                        <Badge color="warning" className="ms-2">Not Found</Badge>
                      </div>
                      <div className="mb-3">
                        <strong>Message:</strong>
                        <p className="mb-0 mt-1 text-muted small">{ctracInfo.message}</p>
                      </div>
                      {ctracInfo.expectedPath && (
                        <div className="mb-3">
                          <strong>Expected Path:</strong>
                          <p className="mb-0 mt-1 text-muted small" style={{ wordBreak: 'break-all' }}>
                            {ctracInfo.expectedPath}
                          </p>
                        </div>
                      )}
                      {ctracInfo.currentDate && (
                        <div className="text-muted small">
                          <strong>Date:</strong> {ctracInfo.currentDate}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="text-muted">No CTRAC package data available</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Local CTRAC Packages Row */}
      <Row>
        {/* Analysis CTRAC package (CSV format) */}
        <Col md={6} className="mb-4">
          <Card>
            <CardHeader>
              <h5 className="mb-0">Analysis CTRAC package (CSV format)</h5>
            </CardHeader>
            <CardBody>
              {loading && !analysisPackageInfo ? (
                <div className="text-center py-3">
                  <Spinner color="primary" />
                </div>
              ) : analysisPackageInfo ? (
                <div>
                  <div className="mb-3">
                    <strong>Status: </strong>
                    {analysisPackageInfo.hasMarker ? (
                      <Badge color="success" className="ms-2">Generated</Badge>
                    ) : analysisPackageInfo.exists ? (
                      <Badge color="warning" className="ms-2">No Marker</Badge>
                    ) : (
                      <Badge color="secondary" className="ms-2">Not Present</Badge>
                    )}
                  </div>
                  <div className="mb-3">
                    <strong>Generated At:</strong>
                    <p className="mb-0 mt-1">{formatDate(analysisPackageInfo.generatedAt)}</p>
                  </div>
                  <div className="mb-3">
                    <strong>Path:</strong>
                    <p className="mb-0 mt-1 text-muted small" style={{ wordBreak: 'break-all' }}>
                      {analysisPackageInfo.path || 'N/A'}
                    </p>
                  </div>
                  <div className="d-flex justify-content-end">
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleDeletePackage('analysis')}
                      disabled={analysisDeleting || !analysisPackageInfo.exists}
                    >
                      {analysisDeleting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Deleting...
                        </>
                      ) : (
                        'Delete'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-muted">No analysis package data available</div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Calculation CTRAC package (CTRAC format) */}
        <Col md={6} className="mb-4">
          <Card>
            <CardHeader>
              <h5 className="mb-0">Calculation CTRAC package (CTRAC format)</h5>
            </CardHeader>
            <CardBody>
              {loading && !calculationPackageInfo ? (
                <div className="text-center py-3">
                  <Spinner color="primary" />
                </div>
              ) : calculationPackageInfo ? (
                <div>
                  <div className="mb-3">
                    <strong>Status: </strong>
                    {calculationPackageInfo.hasMarker ? (
                      <Badge color="success" className="ms-2">Generated</Badge>
                    ) : calculationPackageInfo.exists ? (
                      <Badge color="warning" className="ms-2">No Marker</Badge>
                    ) : (
                      <Badge color="secondary" className="ms-2">Not Present</Badge>
                    )}
                  </div>
                  <div className="mb-3">
                    <strong>Generated At:</strong>
                    <p className="mb-0 mt-1">{formatDate(calculationPackageInfo.generatedAt)}</p>
                  </div>
                  <div className="mb-3">
                    <strong>Path:</strong>
                    <p className="mb-0 mt-1 text-muted small" style={{ wordBreak: 'break-all' }}>
                      {calculationPackageInfo.path || 'N/A'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <strong>Folders inside Calculation:</strong>
                    {Array.isArray(calculationPackageInfo.folders) && calculationPackageInfo.folders.length > 0 ? (
                      <ul className="list-group mt-2">
                        {calculationPackageInfo.folders.map((folder) => (
                          <li
                            key={folder.Path}
                            className="list-group-item d-flex justify-content-between align-items-start"
                          >
                            <div className="me-3" style={{ minWidth: 0, flex: 1 }}>
                              <div
                                className="fw-semibold text-break"
                                style={{ wordBreak: 'break-all' }}
                                title={folder.Path}
                              >
                                {folder.Name}
                              </div>
                              <div
                                className="text-muted small"
                                style={{ wordBreak: 'break-all' }}
                              >
                                {folder.Path}
                              </div>
                            </div>
                            <div className="text-end text-muted small flex-shrink-0">
                              <div>
                                {folder.FileCount} file{folder.FileCount === 1 ? '' : 's'}
                              </div>
                              <div>{formatBytes(folder.TotalSizeBytes)}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mb-0 mt-2 text-muted small">
                        {calculationPackageInfo.exists
                          ? 'The Calculation folder exists but contains no subfolders.'
                          : 'The Calculation folder does not exist yet.'}
                      </p>
                    )}
                  </div>
                  <div className="d-flex justify-content-end">
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleDeletePackage('calculation')}
                      disabled={calculationDeleting || !calculationPackageInfo.exists}
                      title={!calculationPackageInfo.exists ? 'Nothing to delete' : 'Recursively remove the entire Calculation workspace (every subfolder and file)'}
                    >
                      {calculationDeleting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Deleting...
                        </>
                      ) : (
                        'Delete'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-muted">No calculation package data available</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Comparison cache storage */}
      <Row>
        <Col md={6} className="mb-4">
          <Card>
            <CardHeader>
              <h5 className="mb-0">
                Comparison storage{' '}
                {comparisonStorageInfo && (
                  <Badge
                    color={comparisonStorageInfo.exists ? 'info' : 'secondary'}
                    className="ms-2"
                  >
                    {comparisonStorageInfo.exists
                      ? `${comparisonStorageInfo.fileCount} file${comparisonStorageInfo.fileCount === 1 ? '' : 's'}`
                      : 'Empty'}
                  </Badge>
                )}
              </h5>
            </CardHeader>
            <CardBody>
              {loading && !comparisonStorageInfo ? (
                <div className="text-center py-3">
                  <Spinner color="primary" />
                </div>
              ) : comparisonStorageInfo ? (
                <div>
                  <div className="mb-3">
                    <strong>Status: </strong>
                    {comparisonStorageInfo.exists ? (
                      <Badge color="success" className="ms-2">Found</Badge>
                    ) : (
                      <Badge color="secondary" className="ms-2">Not Present</Badge>
                    )}
                  </div>
                  <div className="mb-3">
                    <strong>Path:</strong>
                    <p
                      className="mb-0 mt-1 text-muted small"
                      style={{ wordBreak: 'break-all' }}
                    >
                      {comparisonStorageInfo.path || 'N/A'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <strong>File Count:</strong>
                    <p className="mb-0 mt-1">
                      {comparisonStorageInfo.fileCount ?? 0}
                    </p>
                  </div>
                  <div className="mb-3">
                    <strong>Total Size:</strong>
                    <p className="mb-0 mt-1">
                      {formatBytes(comparisonStorageInfo.totalSizeBytes)}
                    </p>
                  </div>
                  <div className="text-muted small mb-3">
                    Holds every <code>Model-&#123;id&#125;-&#123;Mode&#125;-&#123;Display|Raw&#125;.csv</code> + sidecar produced by the "Compare Spreadsheets" admin page.
                  </div>
                  <div className="d-flex justify-content-end">
                    <Button
                      color="danger"
                      size="sm"
                      onClick={handleDeleteComparisonStorage}
                      disabled={
                        comparisonDeleting ||
                        !comparisonStorageInfo.exists ||
                        (comparisonStorageInfo.fileCount ?? 0) === 0
                      }
                    >
                      {comparisonDeleting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Deleting...
                        </>
                      ) : (
                        'Delete'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-muted">No comparison storage data available</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Cleanup Card */}
      <Row>
        <Col md={12}>
          <Card>
            <CardHeader className="bg-danger text-white">
              <h5 className="mb-0">Danger Zone</h5>
            </CardHeader>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6>Cleanup WiseApp AppDirectory</h6>
                  <p className="text-muted mb-0">
                    This will permanently delete all files in the WiseApp AppDirectory folder.
                    This action cannot be undone.
                  </p>
                  <p className="text-muted small mb-0 mt-1">
                    <strong>Target:</strong> {healthStatus?.appDirectory ?? 'N/A'}
                  </p>
                </div>
                <Button
                  color="danger"
                  onClick={handleCleanup}
                  disabled={cleanupLoading}
                  size="lg"
                >
                  {cleanupLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Cleaning...
                    </>
                  ) : (
                    'Cleanup Directory'
                  )}
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

    </div>
  );
};
